/* eslint-disable react/jsx-no-bind */
import moment from "moment";
import { useQuery } from "react-query";
import { useSelector, shallowEqual } from "react-redux";
import { Highcharts } from "extensions/highcharts";
import {
    ColumnSeries,
    HighchartsChart,
    Chart,
    LineSeries,
    Tooltip,
    HighchartsProvider,
    XAxis,
    YAxis
} from "react-jsx-highcharts";
import { fetchEmails } from "common/actions/email";
import { ErrorView } from "common/components/error";
import { Placeholder } from "common/components/placeholder";
import { rate } from "common/formats";

import { jsxSharedTooltip, plotOptions, dateTimeLabelFormats, getTickInterval, computeSingleSeria } from "common/components/charts/react/utils";

import { dataSelectors } from "./helpers";

const RateAxisProps = {
    labels: {
        formatter: ({ value }) => rate(value * 100)
    },
    opposite: true,
    title: {
        text: "Metric rates"
    }
};

const CountAxisProps = {
    labels: {
        format: "{value:,.0f}"
    },
    title: {
        text: "Event counts"
    }
};

export function Trend() {
    const queryDeps = useSelector((state) => ({
        qualifiers: state.emails.qualifiers,
        date_range: state.common.date_range
    }), shallowEqual);
    const { data: trendline, status } = useQuery(["emails", { pivots: ["date_time"], ...queryDeps }], fetchEmails);

    if (status === "idle" || status === "loading") {
        return <Placeholder />;
    }

    if (status === "error") {
        return <ErrorView error={new Error("Could not fetch data for trend chart.")} inline />;
    }

    if (trendline && !trendline.length) {
        return (
            <ErrorView
                header="No data"
                message="Your search returned no results."
                captureInSentry={false}
                custom
                hideDetails
                actions={false}
            />
        );
    }
    
    const rateSeries = [
        ["click_count", "delivered_count"], // compare `clicks` and `opens` against only successfully delivered emails.
        ["bounce_count", "sent_count"], // bounced emails aren't included in `delivered_count`.
        ["open_count", "delivered_count"]
    ].map(([metric, total]) => computeSingleSeria(trendline)({
        pointFormat: (point) => [
            +new Date(point.date_time),
            1 - (point[total] - point[metric]) / point[total]
        ],
        sorted: false,
        name: dataSelectors[metric].title.replace("count", "rate"),
        dataFormatter: (v) => rate(v * 100)
    }));
    
    // Display counts as (stacked) bars.
    const uniqueSeries = computeSingleSeria(trendline)({
        pointFormat: (point) => [
            +new Date(point.date_time),
            point.delivered_count
        ],
        sorted: false,
        name: "Delivered",
        dataFormatter: dataSelectors.delivered_count.formatter
    });
    
    const nonUniqueSeries = computeSingleSeria(trendline)({
        pointFormat: (point) => [
            +new Date(point.date_time),
            point.sent_count - point.delivered_count
        ],
        sorted: false,
        name: "Sent but not delivered",
        dataFormatter: dataSelectors.delivered_count.formatter
    });
    
    return (
        <HighchartsProvider Highcharts={Highcharts}>
            <HighchartsChart plotOptions={plotOptions} >
                <Chart animation={false} height={250} backgroundColor="transparent" />
                <Tooltip
                    formatter={function() {
                        return jsxSharedTooltip({
                            showPercentOfTotal: false,
                            points: this.points.reverse(),
                            width: 350,
                            header: `${moment.utc(this.x).format("MMM DD, YYYY")}`,
                            bulletIcon: "square"
                        });
                    }}
                    shared
                    crosshairs
                    useHTML
                    shadow={false}
                    backgroundColor="#ffffff"
                    borderRadius={2}
                    dateTimeLabelFormats={dateTimeLabelFormats}
                />
                
                <Tooltip
                    shared
                    crosshairs
                    dateTimeLabelFormats={dateTimeLabelFormats}
                />
                
                <XAxis
                    type="datetime"
                    dateTimeLabelFormats={dateTimeLabelFormats}
                    {...getTickInterval("day")}
                >
                    <YAxis key="counts" {...CountAxisProps}>
                        <ColumnSeries key="non_unique" {...nonUniqueSeries} stacking="normal" />
                        <ColumnSeries key="unique" {...uniqueSeries} stacking="normal" />
                    </YAxis>
                    
                    <YAxis key="rates" {...RateAxisProps}>
                        { rateSeries.map((props, i) => <LineSeries key={props.name} {...props} />) }
                    </YAxis>
                </XAxis>
            </HighchartsChart>
        </HighchartsProvider>
    );
}
